(() => {
  const root = document.documentElement;
  if (root.dataset.pcTypewriterEnabled === 'false') return;

  const typingSpeed = Number(root.dataset.pcTypewriterSpeed || 70);
  const deleteSpeed = Number(root.dataset.pcTypewriterDeleteSpeed || 40);
  const holdDelay = Number(root.dataset.pcTypewriterPause || 1500);
  const nextDelay = Number(root.dataset.pcTypewriterNextDelay || 300);
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const selectors = [
    '.content-for-layout [class*="__heading-highlight"]',
    '.content-for-layout section[class^="pc-"] h1 > em',
    '.content-for-layout section[class^="pc-"] h2 > em',
    '.content-for-layout section[class*=" pc-"] h1 > em',
    '.content-for-layout section[class*=" pc-"] h2 > em'
  ];
  const selector = selectors.join(',');
  const activeSelector = selectors.map((item) => `${item}.pc-typewriter`).join(',');

  // Rotating words are separated with a pipe in the theme editor, e.g.
  // "Shopify | E-commerce". The pipe is only a separator and is never shown.
  // Full stops are treated as normal punctuation, so "growth." stays "growth.".
  const SEPARATOR = '|';

  const splitPhrases = (value) => {
    const text = (value || '').trim();
    if (!text) return [];
    if (!text.includes(SEPARATOR)) return [text];
    const parts = text.split(SEPARATOR).map((part) => part.trim()).filter(Boolean);
    return parts.length ? parts : [];
  };

  // Liquid renders only the first phrase as visible text (so the separator is
  // never painted, even before this script runs) and keeps the full value in
  // data-pc-typewriter-text. Older markup without the attribute still works.
  const sourceText = (el) => {
    const fromData = el.getAttribute('data-pc-typewriter-text');
    return (fromData !== null && fromData.trim() !== '' ? fromData : el.textContent).trim();
  };

  const measure = (el, phrases) => {
    if (!el.parentElement) return;
    const cs = getComputedStyle(el);
    const probe = document.createElement('span');
    Object.assign(probe.style, {
      position: 'fixed',
      left: '-10000px',
      top: '0',
      visibility: 'hidden',
      whiteSpace: 'nowrap',
      fontFamily: cs.fontFamily,
      fontSize: cs.fontSize,
      fontWeight: cs.fontWeight,
      fontStyle: cs.fontStyle,
      letterSpacing: cs.letterSpacing,
      lineHeight: cs.lineHeight
    });
    document.body.appendChild(probe);
    let maxWidth = 0;
    let longestPhrase = phrases[0] || '';
    phrases.forEach((phrase) => {
      probe.textContent = phrase;
      const width = probe.getBoundingClientRect().width;
      if (width > maxWidth) {
        maxWidth = width;
        longestPhrase = phrase;
      }
    });
    probe.remove();
    const parentWidth = Math.max(0, el.parentElement.getBoundingClientRect().width);
    if (maxWidth && parentWidth) {
      el.style.setProperty('--pc-typewriter-min-width', `${Math.min(Math.ceil(maxWidth + 3), Math.floor(parentWidth))}px`);
    }

    // Keep the animated token itself from collapsing while deleting.
    const currentHeight = el.getBoundingClientRect().height;
    const lineHeight = Number.parseFloat(cs.lineHeight);
    const minHeight = Math.max(currentHeight || 0, Number.isFinite(lineHeight) ? lineHeight : 0);
    if (minHeight) el.style.setProperty('--pc-typewriter-min-height', `${Math.ceil(minHeight)}px`);

    // Also reserve the complete heading height. This is what prevents Portfolio
    // and About headings from squeezing upward when the highlighted phrase is
    // temporarily empty, and makes them behave like the Reviews heading.
    const heading = el.closest('h1,h2,h3,h4,h5,h6');
    if (heading) {
      if (!el.dataset.pcTypewriterProbeId) {
        el.dataset.pcTypewriterProbeId = `pc-tw-${Math.random().toString(36).slice(2)}`;
      }
      heading.classList.add('pc-typewriter-heading');
      const rect = heading.getBoundingClientRect();
      if (rect.width > 0) {
        const clone = heading.cloneNode(true);
        const cloneTarget = clone.querySelector(`[data-pc-typewriter-probe-id="${el.dataset.pcTypewriterProbeId}"]`);
        if (cloneTarget) {
          cloneTarget.textContent = longestPhrase;
          cloneTarget.classList.remove('pc-typewriter');
          cloneTarget.style.minWidth = '0';
          cloneTarget.style.minHeight = '0';
        }
        Object.assign(clone.style, {
          position: 'fixed',
          left: '-10000px',
          top: '0',
          visibility: 'hidden',
          width: `${rect.width}px`,
          minHeight: '0',
          height: 'auto'
        });
        document.body.appendChild(clone);
        const fullHeight = clone.getBoundingClientRect().height;
        clone.remove();
        if (fullHeight) heading.style.setProperty('--pc-typewriter-heading-min-height', `${Math.ceil(fullHeight)}px`);
      }
    }
  };

  const setup = (el) => {
    if (!(el instanceof HTMLElement) || el.dataset.pcTypewriterReady === 'true') return;

    const localScope = el.closest('[data-pc-typewriter-enabled]');
    if (localScope && localScope.dataset.pcTypewriterEnabled === 'false') return;

    const original = sourceText(el);
    const phrases = splitPhrases(original);
    if (!phrases.length) {
      el.textContent = '';
      return;
    }

    el.dataset.pcTypewriterReady = 'true';
    el.dataset.pcTypewriterOriginal = original;
    el.classList.add('pc-typewriter');
    measure(el, phrases);

    if (reduceMotion) {
      el.textContent = phrases[0];
      el.classList.add('pc-typewriter--static');
      return;
    }

    let phraseIndex = 0;
    let charIndex = 0;
    let deleting = false;
    let timer;

    const tick = () => {
      if (!el.isConnected) {
        if (timer) window.clearTimeout(timer);
        return;
      }
      const phrase = phrases[phraseIndex];
      if (!deleting) {
        charIndex += 1;
        el.textContent = phrase.slice(0, charIndex);
        if (charIndex >= phrase.length) {
          deleting = true;
          timer = window.setTimeout(tick, holdDelay);
        } else {
          timer = window.setTimeout(tick, typingSpeed);
        }
      } else {
        charIndex -= 1;
        el.textContent = phrase.slice(0, Math.max(0, charIndex));
        if (charIndex <= 0) {
          deleting = false;
          phraseIndex = (phraseIndex + 1) % phrases.length;
          timer = window.setTimeout(tick, nextDelay);
        } else {
          timer = window.setTimeout(tick, deleteSpeed);
        }
      }
    };

    el.textContent = '';
    timer = window.setTimeout(tick, 180);
  };

  const init = (scope = document) => scope.querySelectorAll?.(selector).forEach(setup);
  const ready = () => {
    init(document);
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        document.querySelectorAll(activeSelector).forEach((el) => {
          const phrases = splitPhrases(el.dataset.pcTypewriterOriginal || '');
          measure(el, phrases);
        });
      }).catch(() => {});
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('resize', () => document.querySelectorAll(activeSelector).forEach((el) => {
    const phrases = splitPhrases(el.dataset.pcTypewriterOriginal || '');
    measure(el, phrases);
  }), { passive: true });
})();
